import {
  getFirebaseApp,
  getFunctionsRegion,
  isFirebaseConfigured,
} from "./firebase";
import { getCurrentUser, hasAccount } from "./auth";
import type {
  Design,
  FengShuiCriterionId,
  FengShuiCriterionResult,
  FengShuiRanking,
  Furniture,
  Opening,
  Vec2,
  WallMount,
} from "./types";
import {
  FURNITURE_PRESETS,
  MOUNT_PRESETS,
  presetToFurniture,
  presetToMount,
} from "./catalog";
import type { FrontKind } from "./catalog";
import type { DoorClearanceFrontKind } from "./doorClearance";
import {
  clampInside,
  clampPolygonInside,
  localToWorld,
  pointInPolygon,
} from "./geom";
import { resolveFrontKind, resolveFurniture } from "./furniture/resolve";
import {
  mountFitsOnWall,
  mountOverlapsMount,
  mountOverlapsOpening,
  wallLength,
  type WallRect,
} from "./wallGeom";
import { furnitureBlocksDoor, furnitureFrontBlocksDoor } from "./doorClearance";
import type { DoorClearanceOpening } from "./doorClearance";
import { interiorObstacleSegments, resolveHostSegment } from "./interiorWalls";
import {
  assignCell,
  cellById,
  computeRoomCells,
  type RoomCell,
} from "./roomCells";
import type { FengShuiApply } from "./store";
import {
  AiAnonymousQuotaExceededError,
  AiQuotaExceededError,
  anonymousQuotaReason,
  isQuotaExceededError,
  recordOptimisticUsage,
  type UsageResponseEnvelope,
} from "./usage";
import { captureException, track } from "./telemetry";
import {
  creditsRemaining,
  isNetworkCallableError,
  noteQuotaReached,
  noteUsageEnvelope,
} from "./aiAnalytics";

/**
 * Client-side wrapper around the `generateFengShuiLayout` callable. Mirrors
 * the schema in functions/src/schema.ts; keep these types in sync.
 */

export type FengShuiMode = "rearrange" | "rearrange_suggest" | "redesign";

export interface FengShuiUpdate {
  id: string;
  x: number;
  y: number;
  rotation: number;
  reason: string;
}

export interface FengShuiAddition {
  presetName: string;
  x: number;
  y: number;
  rotation: number;
  reason: string;
}

export interface FengShuiRemoval {
  id: string;
  reason: string;
}

/** Move an existing wall mount: pick it up and re-mount it (potentially on
 *  a different wall, at a different height). */
export interface FengShuiMountUpdate {
  id: string;
  wallIndex: number;
  alongWall: number;
  fromFloor: number;
  reason: string;
}

/** Hang a brand-new mount picked from the {@link MOUNT_PRESETS} catalog. */
export interface FengShuiMountAddition {
  presetName: string;
  wallIndex: number;
  alongWall: number;
  fromFloor: number;
  reason: string;
}

export interface FengShuiMountRemoval {
  id: string;
  reason: string;
}

/** Count of suggestions dropped by post-validation (kept on the wire so the
 *  UI can render "N suggestions didn't fit and were skipped"). Mount fields
 *  are optional so the client tolerates an older deployment that hasn't
 *  shipped the mount support yet. */
export interface FengShuiFilteredCounts {
  updates: number;
  additions: number;
  mountUpdates?: number;
  mountAdditions?: number;
}

export interface FengShuiSuggestion {
  updates: FengShuiUpdate[];
  additions: FengShuiAddition[];
  removals: FengShuiRemoval[];
  /** New: mount moves/additions/removals. Default to [] when an older
   *  deployment doesn't return them. */
  mountUpdates: FengShuiMountUpdate[];
  mountAdditions: FengShuiMountAddition[];
  mountRemovals: FengShuiMountRemoval[];
  overall: string;
  /** Defensive: missing on responses from older deployments. */
  filtered?: FengShuiFilteredCounts;
}

export class FengShuiUnavailableError extends Error {
  constructor() {
    super(
      "Feng Shui is unavailable: Firebase is not configured. Set VITE_FIREBASE_* env vars."
    );
    this.name = "FengShuiUnavailableError";
  }
}

/**
 * Require a registered account before a metered AI call. Anonymous sessions
 * are rejected the same way as signed-out visitors: a new anonymous uid
 * (clearing storage) must not receive another free allowance. Throws
 * {@link FengShuiSignInRequiredError} so the sheet can show its sign-in
 * prompt. The Cloud Function enforces the same rule.
 */
export function requireSessionForAi(): void {
  if (hasAccount(getCurrentUser())) return;
  throw new FengShuiSignInRequiredError();
}

export class FengShuiSignInRequiredError extends Error {
  constructor() {
    super("Sign in to use Feng Shui.");
    this.name = "FengShuiSignInRequiredError";
  }
}

/** Thrown when the backend rejects the call with `permission-denied` —
 *  the caller is signed in but the backend explicitly refused on a
 *  premium-tier basis. After the usage-based billing migration this is
 *  effectively only thrown by older function deployments that still gate
 *  on `requirePremium`; the post-migration AI handlers serve free users
 *  (with a small monthly allowance) and the typed error for hitting
 *  that allowance is {@link AiQuotaExceededError}.
 *
 *  Kept exported for back-compat with callers that still branch on it. */
export class FengShuiPremiumRequiredError extends Error {
  constructor() {
    super("Upgrade to Premium for more Feng Shui layouts.");
    this.name = "FengShuiPremiumRequiredError";
  }
}

/** Re-export so the AI sheets can `import { AiQuotaExceededError }` from
 *  the same module that throws it. The fact that the actual class lives
 *  in `./usage` is an implementation detail. */
export { AiQuotaExceededError, AiAnonymousQuotaExceededError } from "./usage";

/** Narrow a thrown value to the `firebase/functions` HttpsError-shaped
 *  payload (`code: "functions/permission-denied"` etc). The Firebase SDK
 *  doesn't export the class itself, so we duck-type. */
function isFunctionsPermissionDenied(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: unknown }).code;
  return code === "functions/permission-denied" || code === "permission-denied";
}

/** Per-offender record on {@link FengShuiInvalidLayoutError}. `kind`
 *  distinguishes furniture (lies outside the room polygon) from mounts
 *  (fall off the wall, overlap an opening, or overlap another mount). */
export interface FengShuiInvalidLayoutItem {
  id: string;
  name: string;
  kind: "furniture" | "mount";
}

/** Thrown by {@link requestFengShuiLayout} when the user's current design has
 *  furniture that lies outside the room polygon, OR a wall mount whose
 *  geometry is invalid (off the wall, overlapping an opening, overlapping
 *  another mount). We block the request because the AI can't sensibly
 *  reason about pieces that don't fit — the user needs to fix them first. */
export class FengShuiInvalidLayoutError extends Error {
  readonly items: FengShuiInvalidLayoutItem[];

  constructor(
    items: FengShuiInvalidLayoutItem[] | { id: string; name: string }[]
  ) {
    // Back-compat: older callers/tests pass plain {id,name} entries (which
    // are always furniture). Default `kind` to "furniture" in that case.
    const normalized: FengShuiInvalidLayoutItem[] = items.map((it) => ({
      id: it.id,
      name: it.name,
      kind: "kind" in it ? it.kind : "furniture",
    }));
    const names = normalized
      .slice(0, 5)
      .map((i) => i.name)
      .join(", ");
    const more =
      normalized.length > 5 ? ` and ${normalized.length - 5} more` : "";
    super(
      `Some pieces don't fit: ${names}${more}. Move them back inside the room (or off conflicting walls) before generating a layout.`
    );
    this.name = "FengShuiInvalidLayoutError";
    this.items = normalized;
  }
}

/** Server-side cap on user-supplied notes (mirrors `notes.max(1000)` in
 *  functions/src/schema.ts). We trim+slice on the client too so a paste of
 *  10kB can't get rejected after a round-trip to the function. */
export const FENG_SHUI_NOTES_MAX_LENGTH = 1000;

export interface FengShuiRequestOptions {
  /** Free-text from the homeowner: extra requirements, dual-use furniture
   *  ("loveseat folds out into a bed at night"), accessibility, must-haves,
   *  etc. Trimmed and clipped to {@link FENG_SHUI_NOTES_MAX_LENGTH} chars. */
  notes?: string;
}

/**
 * Call the Firebase function and return the model's suggestion. Throws
 * {@link FengShuiUnavailableError} if Firebase isn't configured (so the UI
 * can render a clear inline message rather than crashing), or
 * {@link FengShuiInvalidLayoutError} if the current design has furniture
 * sitting outside the room polygon.
 */
export async function requestFengShuiLayout(
  design: Design,
  mode: FengShuiMode,
  options: FengShuiRequestOptions = {}
): Promise<FengShuiSuggestion> {
  if (!isFirebaseConfigured()) throw new FengShuiUnavailableError();
  const app = getFirebaseApp();
  if (!app) throw new FengShuiUnavailableError();

  // The Cloud Function rejects callers without a registered account.
  // Mirror that here so the sheet can show its sign-in prompt.
  await requireSessionForAi();

  // Pre-flight: refuse to send when any current piece doesn't fit. The AI
  // would otherwise either invent positions for already-broken pieces or
  // anchor its plan around them.
  const offenders = collectInvalidLayoutItems(design);
  if (offenders.length > 0) {
    track("ai_layout_failed", { mode, reason: "invalid_layout" });
    throw new FengShuiInvalidLayoutError(offenders);
  }

  // Dynamic import so the Firebase Functions SDK doesn't bloat the initial bundle.
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const functions = getFunctions(app, getFunctionsRegion());

  const trimmedNotes = (options.notes ?? "")
    .trim()
    .slice(0, FENG_SHUI_NOTES_MAX_LENGTH);

  // Partition the plan into the rooms (cells) its interior walls form, and
  // assign each existing piece to one. A piece that overlaps a room is locked
  // to it; a piece clear of every room is free (`roomId` null) and may be
  // placed in any room. Both the model and the server use this to keep the
  // output inside a valid room.
  const cells = computeRoomCells(design.room);
  const furnitureRoomIds = new Map<string, string | null>(
    design.furniture.map((f) => [
      f.id,
      assignCell(worldFootprint(f, f.x, f.y, f.rotation), cells),
    ])
  );

  // Denormalize `frontKind` and the resolved footprint polygon onto each
  // piece so the server doesn't have to join against the catalog or know
  // about templates. The server's validators use `footprint` when present
  // and fall back to the width × depth rectangle otherwise.
  const enrichedFurniture = design.furniture.map((f) => ({
    ...f,
    frontKind: resolveFrontKind(f),
    footprint: wireFootprint(f),
    roomId: furnitureRoomIds.get(f.id) ?? null,
  }));

  const payload: {
    /** Idempotency key — the server uses this to dedup the usage-counter
     *  increment + Stripe meter event on retry. Generated per call so
     *  every distinct user-initiated request gets its own credit
     *  exactly once, no matter how many times the underlying network
     *  retries. */
    requestId: string;
    room: Design["room"];
    furniture: (Design["furniture"][number] & {
      frontKind: FrontKind;
      roomId: string | null;
    })[];
    mounts: Design["mounts"];
    openings: Design["openings"];
    unit: Design["unit"];
    mode: FengShuiMode;
    /** The rooms (cells) the interior walls form; each piece's `roomId`
     *  references one of these. Omitted when the room has a single cell. */
    layoutRooms: { id: string; polygon: Vec2[] }[];
    /** Interior-wall centerline segments, so the model knows where the
     *  dividing walls are when it reasons about each room. */
    interiorSegments: { a: Vec2; b: Vec2 }[];
    catalog: {
      name: string;
      width: number;
      depth: number;
      height: number;
      frontKind: FrontKind;
      footprint: Vec2[];
    }[];
    mountCatalog: {
      name: string;
      category: WallMount["category"];
      width: number;
      height: number;
      defaultFromFloor: number;
    }[];
    notes?: string;
  } = {
    requestId: makeRequestId(),
    room: design.room,
    furniture: enrichedFurniture,
    mounts: design.mounts.filter((m) => !m.interiorWallId),
    openings: design.openings.filter((o) => !o.interiorWallId),
    unit: design.unit,
    mode,
    layoutRooms: cells.map((c) => ({ id: c.id, polygon: c.polygon })),
    interiorSegments: interiorObstacleSegments(design.room),
    // Send dims + frontKind with each preset so the server (and the model)
    // can size additions correctly, validate fit, and reason about which
    // edge of every piece faces the user without a duplicate catalog.
    catalog: FURNITURE_PRESETS.map((p) => ({
      name: p.name,
      width: p.width,
      depth: p.depth,
      height: p.height,
      frontKind: p.frontKind,
      // Only non-rectangular presets need the outline on the wire; a
      // rectangle is implied by width × depth and saves payload.
      footprint: wireFootprint({
        ...presetToFurniture(p, 0, 0),
        id: "__cat__",
      } as Furniture),
    })),
    mountCatalog: MOUNT_PRESETS.map((p) => ({
      name: p.name,
      category: p.category,
      width: p.width,
      height: p.height,
      defaultFromFloor: p.defaultFromFloor,
    })),
  };
  if (trimmedNotes.length > 0) payload.notes = trimmedNotes;

  // The Firebase Functions client defaults to a 70s timeout, which is shorter
  // than the model's worst-case generation time. The server is configured for
  // 180s in functions/src/index.ts; mirror that here so the client doesn't
  // abort early with `deadline-exceeded` while the function is still working.
  const callable = httpsCallable<
    typeof payload,
    Partial<FengShuiSuggestion> & { usage?: UsageResponseEnvelope }
  >(functions, "generateFengShuiLayout", { timeout: 180_000 });
  track("ai_layout_requested", { mode });
  const startedAt = Date.now();
  let res;
  try {
    res = await callable(payload);
  } catch (err) {
    // Quota exhaustion comes back as `resource-exhausted` — the typed
    // error lets the AI sheets show the upsell. Check this BEFORE the
    // permission-denied path, since older deployments may still send
    // permission-denied for non-premium callers (the wrapped error is
    // semantically the same: "you can't continue without paying").
    const anon = anonymousQuotaReason(err);
    if (anon) {
      track("ai_layout_failed", { mode, reason: "quota" });
      throw new AiAnonymousQuotaExceededError(anon);
    }
    if (isQuotaExceededError(err)) {
      track("ai_layout_failed", { mode, reason: "quota" });
      noteQuotaReached("free");
      throw new AiQuotaExceededError();
    }
    if (isFunctionsPermissionDenied(err)) {
      track("ai_layout_failed", { mode, reason: "premium_required" });
      throw new FengShuiPremiumRequiredError();
    }
    if (isNetworkCallableError(err)) {
      track("ai_layout_failed", { mode, reason: "network" });
      throw err;
    }
    track("ai_layout_failed", { mode, reason: "unknown" });
    captureException(err, { context: "requestFengShuiLayout", mode });
    throw err;
  }
  // The new metered server returns the AI fields PLUS a `usage` envelope.
  // Older deployments don't send `usage`; skip the optimistic update in
  // that case (the Firestore snapshot will catch up if metering is on).
  if (res.data.usage) recordOptimisticUsage(res.data.usage);
  const validated = validateSuggestionFit(
    normalizeSuggestion(res.data),
    design,
    cells,
    furnitureRoomIds
  );
  track("ai_layout_succeeded", {
    mode,
    updates: validated.updates.length,
    additions: validated.additions.length,
    latency_ms: Date.now() - startedAt,
    credits_remaining: creditsRemaining(res.data.usage),
  });
  noteUsageEnvelope(res.data.usage);
  return validated;
}

/** Per-request idempotency key. Crypto-grade UUID where supported (modern
 *  browsers + jsdom 22+); falls back to a non-cryptographic random in old
 *  environments — the server tolerates either (it only needs the string
 *  to be stable across retries of the same logical request). */
function makeRequestId(): string {
  if (
    typeof globalThis !== "undefined" &&
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** Maximum footprint vertices sent on the wire (mirrors the server schema). */
const WIRE_FOOTPRINT_MAX_POINTS = 256;

/**
 * The piece's true footprint outline (footprint-local inches), rounded to
 * 1/100" and decimated to the wire cap. Circles resolve to ~48 vertices, so
 * decimation only ever kicks in for pathological free-form sketches.
 */
export function wireFootprint(f: Furniture): Vec2[] {
  const fp = resolveFurniture(f).footprint;
  const step = Math.max(1, Math.ceil(fp.length / WIRE_FOOTPRINT_MAX_POINTS));
  const out: Vec2[] = [];
  for (let i = 0; i < fp.length; i += step) {
    out.push({
      x: Math.round(fp[i].x * 100) / 100,
      y: Math.round(fp[i].y * 100) / 100,
    });
  }
  return out;
}

/** World-space footprint of a piece at a given placement. */
function worldFootprint(
  f: Furniture,
  cx: number,
  cy: number,
  rotation: number
): Vec2[] {
  return localToWorld(resolveFurniture(f).footprint, cx, cy, rotation);
}

/** Door-clearance input. Interior doors carry their segment so the swing
 *  isn't measured along outline wall 0. */
function clearanceDoor(design: Design, opening: Opening): DoorClearanceOpening {
  const base: DoorClearanceOpening = {
    kind: opening.kind,
    name: opening.name,
    wallIndex: opening.wallIndex,
    alongWall: opening.alongWall,
    width: opening.width,
    ...(opening.style === "cutout" ? { style: "cutout" as const } : {}),
  };
  if (!opening.interiorWallId) return base;
  const seg = resolveHostSegment(design.room, opening);
  if (!seg) return base;
  return { ...base, segment: { a: seg.a, b: seg.b } };
}

/** Collect every existing piece (furniture or mount) whose geometry isn't
 *  valid in the current design, for the pre-flight error sheet. */
function collectInvalidLayoutItems(
  design: Design
): FengShuiInvalidLayoutItem[] {
  const offenders: FengShuiInvalidLayoutItem[] = [];
  const polygon = design.room.points;
  const doorOpenings = design.openings
    .filter((o) => o.kind === "door")
    .map((o) => clearanceDoor(design, o));
  for (const f of design.furniture) {
    // Furniture bounds are NOT a pre-flight failure: a piece sitting on the
    // wall, half in, or completely outside the room is allowed into the
    // request. The generated layout is what must land inside a room — see
    // `validateSuggestionFit`, which locks each piece to its room (or any
    // room, if it was clear of them all) and clamps the output.
    //
    // Drawer/door pieces whose pull-out strip lands in a door swing zone
    // still block: the AI can't sensibly plan around a piece the user can't
    // actually open.
    const fk: DoorClearanceFrontKind = resolveFrontKind(f);
    let frontBlocked = false;
    for (const door of doorOpenings) {
      if (
        furnitureFrontBlocksDoor(
          f.x,
          f.y,
          f.width,
          f.depth,
          f.rotation,
          fk,
          door,
          polygon
        )
      ) {
        frontBlocked = true;
        break;
      }
    }
    if (frontBlocked) {
      offenders.push({ id: f.id, name: f.name, kind: "furniture" });
    }
  }
  // Wall mounts: per-wall fit + non-overlap with openings on the same wall +
  // non-overlap with other mounts on the same wall.
  const wallCount = polygon.length;
  const wallLens = polygon.map((_, i) => wallLength(polygon, i));
  const openingsByWall = new Map<number, WallRect[]>();
  for (const o of design.openings) {
    if (o.interiorWallId) continue;
    if (o.wallIndex < 0 || o.wallIndex >= wallCount) continue;
    const list = openingsByWall.get(o.wallIndex) ?? [];
    list.push({
      alongWall: o.alongWall,
      fromFloor: o.fromFloor,
      width: o.width,
      height: o.height,
    });
    openingsByWall.set(o.wallIndex, list);
  }
  const mountsByWall = new Map<number, WallMount[]>();
  for (const m of design.mounts) {
    if (m.interiorWallId) continue;
    const list = mountsByWall.get(m.wallIndex) ?? [];
    list.push(m);
    mountsByWall.set(m.wallIndex, list);
  }
  for (const m of design.mounts) {
    if (m.interiorWallId) {
      const seg = resolveHostSegment(design.room, m);
      const fits =
        !!seg &&
        mountFitsOnWall(
          m.alongWall,
          m.fromFloor,
          m.width,
          m.height,
          seg.len,
          design.room.ceilingHeight
        );
      if (!fits) offenders.push({ id: m.id, name: m.name, kind: "mount" });
      continue;
    }
    const wallLen = wallLens[m.wallIndex];
    if (wallLen === undefined || m.wallIndex < 0 || m.wallIndex >= wallCount) {
      offenders.push({ id: m.id, name: m.name, kind: "mount" });
      continue;
    }
    if (
      !mountFitsOnWall(
        m.alongWall,
        m.fromFloor,
        m.width,
        m.height,
        wallLen,
        design.room.ceilingHeight
      )
    ) {
      offenders.push({ id: m.id, name: m.name, kind: "mount" });
      continue;
    }
    let bad = false;
    for (const o of openingsByWall.get(m.wallIndex) ?? []) {
      if (mountOverlapsOpening(m, o)) {
        offenders.push({ id: m.id, name: m.name, kind: "mount" });
        bad = true;
        break;
      }
    }
    if (bad) continue;
    for (const other of mountsByWall.get(m.wallIndex) ?? []) {
      if (other.id === m.id) continue;
      if (mountOverlapsMount(m, other)) {
        offenders.push({ id: m.id, name: m.name, kind: "mount" });
        break;
      }
    }
  }
  return offenders;
}

/** Coerce a possibly-partial response (older deployment, wire glitch) into
 *  a fully-populated {@link FengShuiSuggestion} with empty mount arrays. */
function normalizeSuggestion(
  raw: Partial<FengShuiSuggestion> | undefined
): FengShuiSuggestion {
  const r = raw ?? ({} as Partial<FengShuiSuggestion>);
  return {
    updates: r.updates ?? [],
    additions: r.additions ?? [],
    removals: r.removals ?? [],
    mountUpdates: r.mountUpdates ?? [],
    mountAdditions: r.mountAdditions ?? [],
    mountRemovals: r.mountRemovals ?? [],
    overall: r.overall ?? "",
    filtered: r.filtered,
  };
}

/** Re-check the server's response against the rooms (cells) the plan forms and
 *  make the output valid. Each update/addition is kept only when its true
 *  footprint lands inside an allowed room (the piece's locked room, or any
 *  room when it was free) and clears door swings; otherwise it's dropped. Then
 *  every existing piece that still sits on a wall, half in, or outside is
 *  clamped into its room so the applied layout is always valid. Defense in
 *  depth: the server runs the same containment check, but it can't clamp (no
 *  footprint resolver), so the correction lives here. Returns a new suggestion
 *  with offenders removed, clamp updates added, and drops merged into
 *  `filtered`. */
function validateSuggestionFit(
  suggestion: FengShuiSuggestion,
  design: Design,
  cells: RoomCell[],
  furnitureRoomIds: Map<string, string | null>
): FengShuiSuggestion {
  const polygon = design.room.points;
  // Never leave the piece without a room to fall into: a valid room always
  // has at least its outline cell.
  const cellList: RoomCell[] =
    cells.length > 0
      ? cells
      : [{ id: "cell:0", polygon: polygon.map((p) => ({ ...p })) }];
  // Existing pieces by id — the resolver gives us their true footprint.
  const infoById = new Map<string, Furniture>(
    design.furniture.map((f) => [f.id, f])
  );

  /** The rooms a piece may be placed in: its locked room, or every room when
   *  it was free (clear of them all on input, or a brand-new addition). */
  function allowedCellsFor(roomId: string | null | undefined): RoomCell[] {
    const locked = cellById(cellList, roomId ?? undefined);
    return locked ? [locked] : cellList;
  }
  // A piece flush against a wall (footprint vertices on the boundary) is a
  // valid, desirable placement, so containment allows touching within a small
  // tolerance but rejects any real protrusion. Cell polygons already exclude
  // the interior-wall thickness, so "inside the cell" also means off the walls.
  const CONTAINMENT_EPS_IN = 0.75;
  function withinCell(world: Vec2[], cellPolygon: Vec2[]): boolean {
    if (cellPolygon.length < 3) return false;
    for (const v of world) {
      if (pointInPolygon(v, cellPolygon)) continue;
      const near = clampInside(v, cellPolygon);
      if (Math.hypot(near.x - v.x, near.y - v.y) > CONTAINMENT_EPS_IN) {
        return false;
      }
    }
    return true;
  }
  /** The first allowed room the footprint sits inside (touching allowed), else null. */
  function fitsSomeCell(world: Vec2[], allowed: RoomCell[]): RoomCell | null {
    for (const cell of allowed) {
      if (withinCell(world, cell.polygon)) return cell;
    }
    return null;
  }
  function cellCentroid(cell: RoomCell): Vec2 {
    let x = 0;
    let y = 0;
    for (const p of cell.polygon) {
      x += p.x;
      y += p.y;
    }
    const n = cell.polygon.length || 1;
    return { x: x / n, y: y / n };
  }

  // Defense in depth: even though the server-side enforceMode drops these,
  // an older deployment (or a future schema drift) might not. Re-check on
  // the client so the user never sees a plant placed in front of a door.
  const doorOpenings = design.openings
    .filter((o) => o.kind === "door")
    .map((o) => clearanceDoor(design, o));
  function blocksAnyDoor(outlineWorld: Vec2[]): boolean {
    if (doorOpenings.length === 0) return false;
    for (const door of doorOpenings) {
      if (furnitureBlocksDoor(outlineWorld, door, polygon)) return true;
    }
    return false;
  }
  function frontBlocksAnyDoor(
    cx: number,
    cy: number,
    width: number,
    depth: number,
    rotation: number,
    frontKind: DoorClearanceFrontKind
  ): boolean {
    if (doorOpenings.length === 0) return false;
    for (const door of doorOpenings) {
      if (
        furnitureFrontBlocksDoor(
          cx,
          cy,
          width,
          depth,
          rotation,
          frontKind,
          door,
          polygon
        )
      ) {
        return true;
      }
    }
    return false;
  }

  let droppedUpdates = 0;
  // Ids the model repositioned validly — those keep their move and are skipped
  // by the clamp pass below.
  const keptUpdateIds = new Set<string>();
  const updates = suggestion.updates.filter((u) => {
    const info = infoById.get(u.id);
    if (!info) return true; // unknown id is the server/enforceMode's job to drop
    const allowed = allowedCellsFor(furnitureRoomIds.get(u.id));
    const uWorld = worldFootprint(info, u.x, u.y, u.rotation);
    const cell = fitsSomeCell(uWorld, allowed);
    const clearsDoors = !!cell && !blocksAnyDoor(uWorld);
    const clearsFront =
      clearsDoors &&
      !frontBlocksAnyDoor(
        u.x,
        u.y,
        info.width,
        info.depth,
        u.rotation,
        resolveFrontKind(info)
      );
    if (!cell || !clearsDoors || !clearsFront) {
      droppedUpdates += 1;
      return false;
    }
    keptUpdateIds.add(u.id);
    return true;
  });

  let droppedAdditions = 0;
  const additions = suggestion.additions.filter((a) => {
    const preset = FURNITURE_PRESETS.find((p) => p.name === a.presetName);
    if (!preset) return true;
    const presetPiece = {
      ...presetToFurniture(preset, a.x, a.y),
      id: "__preset__",
    } as Furniture;
    // A new piece may land in any room, but must sit fully inside one.
    const aWorld = worldFootprint(presetPiece, a.x, a.y, a.rotation);
    const cell = fitsSomeCell(aWorld, cellList);
    const clearsDoors = !!cell && !blocksAnyDoor(aWorld);
    const clearsFront =
      clearsDoors &&
      !frontBlocksAnyDoor(
        a.x,
        a.y,
        preset.width,
        preset.depth,
        a.rotation,
        preset.frontKind
      );
    if (!cell || !clearsDoors || !clearsFront) droppedAdditions += 1;
    return !!cell && clearsDoors && clearsFront;
  });

  // Clamp pass: every existing piece must end inside a room. A piece the model
  // moved validly keeps that move; one whose move was dropped reverts to its
  // current pose. Any piece whose resulting pose is still illegal — it was
  // sitting on a wall, half in, or completely outside — is nudged into its
  // room with the shared clamp helper, keeping its locked room (or dropping
  // into the nearest room when it was free). A piece that can't be made to fit
  // is left as-is, and a clamp that would land in a door swing is not applied.
  function clampIntoCells(
    f: Furniture,
    local: Vec2[],
    allowed: RoomCell[]
  ): { x: number; y: number } | null {
    const center = { x: f.x, y: f.y };
    const fk = resolveFrontKind(f);
    const ordered = [...allowed].sort((a, b) => {
      const ca = cellCentroid(a);
      const cb = cellCentroid(b);
      return (
        Math.hypot(ca.x - center.x, ca.y - center.y) -
        Math.hypot(cb.x - center.x, cb.y - center.y)
      );
    });
    for (const cell of ordered) {
      const c = clampPolygonInside(center, local, f.rotation, cell.polygon);
      const cWorld = worldFootprint(f, c.x, c.y, f.rotation);
      if (!withinCell(cWorld, cell.polygon)) continue;
      if (blocksAnyDoor(cWorld)) continue;
      if (frontBlocksAnyDoor(c.x, c.y, f.width, f.depth, f.rotation, fk))
        continue;
      return c;
    }
    return null;
  }

  const clampUpdates: FengShuiUpdate[] = [];
  for (const f of design.furniture) {
    if (keptUpdateIds.has(f.id)) continue; // model placed it validly
    const local = resolveFurniture(f).footprint;
    const allowed = allowedCellsFor(furnitureRoomIds.get(f.id));
    const curWorld = worldFootprint(f, f.x, f.y, f.rotation);
    const curCell = fitsSomeCell(curWorld, allowed);
    const curClears =
      !!curCell &&
      !blocksAnyDoor(curWorld) &&
      !frontBlocksAnyDoor(
        f.x,
        f.y,
        f.width,
        f.depth,
        f.rotation,
        resolveFrontKind(f)
      );
    if (curClears) continue; // already valid — leave it
    const fixed = clampIntoCells(f, local, allowed);
    if (fixed) {
      clampUpdates.push({
        id: f.id,
        x: fixed.x,
        y: fixed.y,
        rotation: f.rotation,
        reason: "Moved fully inside the room.",
      });
    }
  }
  const allUpdates = [...updates, ...clampUpdates];

  // ---- Mount validation -----------------------------------------------

  const wallCount = polygon.length;
  const ceilingHeight = design.room.ceilingHeight;
  const wallLens = polygon.map((_, i) => wallLength(polygon, i));
  const openingsByWall = new Map<number, WallRect[]>();
  for (const o of design.openings) {
    if (o.interiorWallId) continue;
    if (o.wallIndex < 0 || o.wallIndex >= wallCount) continue;
    const list = openingsByWall.get(o.wallIndex) ?? [];
    list.push({
      alongWall: o.alongWall,
      fromFloor: o.fromFloor,
      width: o.width,
      height: o.height,
    });
    openingsByWall.set(o.wallIndex, list);
  }

  type MountPlacement = WallRect & { wallIndex: number; id?: string };
  const placementsByWall = new Map<number, MountPlacement[]>();
  for (const m of design.mounts) {
    if (m.interiorWallId) continue;
    const list = placementsByWall.get(m.wallIndex) ?? [];
    list.push({
      id: m.id,
      wallIndex: m.wallIndex,
      alongWall: m.alongWall,
      fromFloor: m.fromFloor,
      width: m.width,
      height: m.height,
    });
    placementsByWall.set(m.wallIndex, list);
  }

  function mountDimsById(id: string): { width: number; height: number } | null {
    const m = design.mounts.find((x) => x.id === id);
    return m ? { width: m.width, height: m.height } : null;
  }
  function placementFits(target: MountPlacement, excludeId?: string): boolean {
    if (target.wallIndex < 0 || target.wallIndex >= wallCount) return false;
    const wallLen = wallLens[target.wallIndex] ?? 0;
    if (
      !mountFitsOnWall(
        target.alongWall,
        target.fromFloor,
        target.width,
        target.height,
        wallLen,
        ceilingHeight
      )
    ) {
      return false;
    }
    for (const o of openingsByWall.get(target.wallIndex) ?? []) {
      if (mountOverlapsOpening(target, o)) return false;
    }
    for (const p of placementsByWall.get(target.wallIndex) ?? []) {
      if (excludeId && p.id === excludeId) continue;
      if (mountOverlapsMount(target, p)) return false;
    }
    return true;
  }
  function removePlacementById(id: string) {
    for (const [wi, list] of placementsByWall) {
      const next = list.filter((p) => p.id !== id);
      if (next.length !== list.length) {
        placementsByWall.set(wi, next);
        return;
      }
    }
  }
  function addPlacement(p: MountPlacement) {
    const list = placementsByWall.get(p.wallIndex) ?? [];
    list.push(p);
    placementsByWall.set(p.wallIndex, list);
  }

  // Strip every updated mount up front so an update doesn't self-collide
  // with its own previous position.
  const updateById = new Map<string, FengShuiMountUpdate>();
  for (const u of suggestion.mountUpdates) {
    if (mountDimsById(u.id)) updateById.set(u.id, u);
  }
  for (const u of updateById.values()) {
    removePlacementById(u.id);
  }

  let droppedMountUpdates = 0;
  const mountUpdates: FengShuiMountUpdate[] = [];
  for (const u of suggestion.mountUpdates) {
    const dims = mountDimsById(u.id);
    if (!dims) {
      // Unknown id — leave it for the server/store to handle.
      mountUpdates.push(u);
      continue;
    }
    const placement: MountPlacement = {
      id: u.id,
      wallIndex: u.wallIndex,
      alongWall: u.alongWall,
      fromFloor: u.fromFloor,
      width: dims.width,
      height: dims.height,
    };
    if (placementFits(placement, u.id)) {
      addPlacement(placement);
      mountUpdates.push(u);
    } else {
      // Restore original placement so subsequent entries see the unmoved mount.
      const orig = design.mounts.find((m) => m.id === u.id);
      if (orig) {
        addPlacement({
          id: orig.id,
          wallIndex: orig.wallIndex,
          alongWall: orig.alongWall,
          fromFloor: orig.fromFloor,
          width: orig.width,
          height: orig.height,
        });
      }
      droppedMountUpdates += 1;
    }
  }

  let droppedMountAdditions = 0;
  const mountAdditions: FengShuiMountAddition[] = [];
  for (const a of suggestion.mountAdditions) {
    const preset = MOUNT_PRESETS.find((p) => p.name === a.presetName);
    if (!preset) {
      // Unknown preset — leave for store/server to filter so we don't
      // double-count it as a geometric drop.
      mountAdditions.push(a);
      continue;
    }
    const placement: MountPlacement = {
      wallIndex: a.wallIndex,
      alongWall: a.alongWall,
      fromFloor: a.fromFloor,
      width: preset.width,
      height: preset.height,
    };
    if (placementFits(placement)) {
      addPlacement(placement);
      mountAdditions.push(a);
    } else {
      droppedMountAdditions += 1;
    }
  }

  const prior = suggestion.filtered ?? { updates: 0, additions: 0 };
  const filtered: FengShuiFilteredCounts = {
    updates: prior.updates + droppedUpdates,
    additions: prior.additions + droppedAdditions,
  };
  const priorMountUpdates = prior.mountUpdates ?? 0;
  const priorMountAdditions = prior.mountAdditions ?? 0;
  if (priorMountUpdates + droppedMountUpdates > 0) {
    filtered.mountUpdates = priorMountUpdates + droppedMountUpdates;
  }
  if (priorMountAdditions + droppedMountAdditions > 0) {
    filtered.mountAdditions = priorMountAdditions + droppedMountAdditions;
  }

  return {
    ...suggestion,
    updates: allUpdates,
    additions,
    mountUpdates,
    mountAdditions,
    filtered,
  };
}

/**
 * Resolve a {@link FengShuiSuggestion} into a {@link FengShuiApply} payload
 * the store can consume. Caller picks which items to apply by filtering
 * the lists first; this function maps preset names → catalog presets,
 * constructs full `Furniture`/`WallMount` objects from additions, preserves
 * each item's `reason`, and assembles the consultation context so the store
 * can record one entry per applied suggestion.
 */
export function buildApplyPayload(
  suggestion: FengShuiSuggestion,
  picked: {
    updateIds: Set<string>;
    additionKeys: Set<string>;
    removalIds: Set<string>;
    mountUpdateIds?: Set<string>;
    mountAdditionKeys?: Set<string>;
    mountRemovalIds?: Set<string>;
  },
  context: { mode: FengShuiMode; notes?: string }
): FengShuiApply {
  const updates = suggestion.updates
    .filter((u) => picked.updateIds.has(u.id))
    .map((u) => ({
      id: u.id,
      x: u.x,
      y: u.y,
      rotation: u.rotation,
      reason: u.reason,
    }));

  const additions: (Omit<Furniture, "id"> & { reason: string })[] = [];
  for (const a of suggestion.additions) {
    const key = additionKey(a);
    if (!picked.additionKeys.has(key)) continue;
    const preset = FURNITURE_PRESETS.find((p) => p.name === a.presetName);
    if (!preset) continue;
    const base = presetToFurniture(preset, a.x, a.y);
    additions.push({ ...base, rotation: a.rotation, reason: a.reason });
  }

  const removals = suggestion.removals
    .filter((r) => picked.removalIds.has(r.id))
    .map((r) => ({ id: r.id, reason: r.reason }));

  const mountUpdateIds = picked.mountUpdateIds ?? new Set<string>();
  const mountAdditionKeys = picked.mountAdditionKeys ?? new Set<string>();
  const mountRemovalIds = picked.mountRemovalIds ?? new Set<string>();

  const mountUpdates = suggestion.mountUpdates
    .filter((u) => mountUpdateIds.has(u.id))
    .map((u) => ({
      id: u.id,
      wallIndex: u.wallIndex,
      alongWall: u.alongWall,
      fromFloor: u.fromFloor,
      reason: u.reason,
    }));

  const mountAdditions: (Omit<WallMount, "id"> & { reason: string })[] = [];
  for (const a of suggestion.mountAdditions) {
    const key = mountAdditionKey(a);
    if (!mountAdditionKeys.has(key)) continue;
    const preset = MOUNT_PRESETS.find((p) => p.name === a.presetName);
    if (!preset) continue;
    const base = presetToMount(preset, a.wallIndex, a.alongWall);
    // The model is allowed to override the preset's `defaultFromFloor`
    // (e.g. lower a TV from 56" to 42" when there's seating below). Trust
    // its `fromFloor` here; the validator already vetted the geometry.
    mountAdditions.push({
      ...base,
      fromFloor: a.fromFloor,
      reason: a.reason,
    });
  }

  const mountRemovals = suggestion.mountRemovals
    .filter((r) => mountRemovalIds.has(r.id))
    .map((r) => ({ id: r.id, reason: r.reason }));

  const trimmedNotes = (context.notes ?? "").trim();
  const consultation: FengShuiApply["consultation"] = {
    mode: context.mode,
    overall: suggestion.overall,
  };
  if (trimmedNotes.length > 0) consultation.notes = trimmedNotes;
  if (suggestion.filtered) consultation.filtered = suggestion.filtered;

  return {
    updates,
    additions,
    removals,
    mountUpdates,
    mountAdditions,
    mountRemovals,
    consultation,
  };
}

/** Unique key for an addition entry (since additions have no id from the model). */
export function additionKey(a: FengShuiAddition): string {
  return `${a.presetName}@${a.x.toFixed(2)},${a.y.toFixed(
    2
  )},${a.rotation.toFixed(1)}`;
}

/** Unique key for a mount addition. Includes `wallIndex` so the same preset
 *  hung on two different walls keeps two distinct picker entries. */
export function mountAdditionKey(a: FengShuiMountAddition): string {
  return `${a.presetName}@wall${a.wallIndex}@${a.alongWall.toFixed(
    2
  )},${a.fromFloor.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Ranking — "Rank my layout"
// ---------------------------------------------------------------------------
//
// Parallel pipeline to `requestFengShuiLayout` that scores the current design
// against a fixed set of feng shui criteria. Wire schema, criterion IDs, and
// weights MUST stay in sync with `functions/src/ranking.ts` on the server.
// ---------------------------------------------------------------------------

/** UI metadata for a single criterion. `id` and `weight` mirror the server's
 *  `CRITERIA_REGISTRY` exactly. */
export interface FengShuiCriterionMeta {
  id: FengShuiCriterionId;
  label: string;
  weight: number;
  /** Short, user-facing description. Shown next to the score bar. */
  description: string;
}

/**
 * Criterion registry shared by every Rank UI surface. Order here is the
 * order cards render in. Weights sum to 100. Mirror this in
 * `functions/src/ranking.ts` — drift breaks the score normalization.
 */
export const RANKING_CRITERIA_META: readonly FengShuiCriterionMeta[] = [
  {
    id: "commanding_position",
    label: "Commanding position",
    weight: 20,
    description:
      "Bed, desk, and primary seating see the main door without aligning with it; solid wall behind.",
  },
  {
    id: "chi_flow",
    label: "qi flow & walkways",
    weight: 15,
    description:
      "36-inch-plus paths, no blocked doors or windows, no back-to-door seating.",
  },
  {
    id: "bagua",
    label: "Bagua alignment",
    weight: 10,
    description:
      "Major pieces sit in appropriate guas relative to the entry door (wealth, fame, relationships).",
  },
  {
    id: "five_elements",
    label: "Five elements balance",
    weight: 10,
    description:
      "Wood, fire, earth, metal, and water are represented — none dominates.",
  },
  {
    id: "doors_windows",
    label: "Doors & windows",
    weight: 15,
    description:
      "Door swing clear, bed not under a window, tall storage not blocking light.",
  },
  {
    id: "sha_chi",
    label: "Sha qi (sharp corners)",
    weight: 10,
    description: "Sharp corners not pointing at beds or primary seating.",
  },
  {
    id: "wall_mounts",
    label: "Wall mount harmony",
    weight: 10,
    description:
      "No mirror facing the bed or front door; TV not directly opposite the bed; art at proper height.",
  },
  {
    id: "clearance",
    label: "Clearance & open space",
    weight: 10,
    description:
      "24-inch-plus clearance around seats; the room doesn't feel overcrowded.",
  },
] as const;

/** Re-export the criterion result shape for callers that don't want to
 *  reach into `./types`. Keeps the import surface symmetric with the
 *  layout side of this module. */
export type { FengShuiCriterionResult, FengShuiRanking } from "./types";

/** Wire response shape from `rankFengShuiLayout`. The persisted
 *  {@link FengShuiRanking} wraps this with a client-generated `id`,
 *  `at` timestamp, and lightweight layout `snapshot`. */
export interface FengShuiRankingResponse {
  criteria: FengShuiCriterionResult[];
  overall: { score: number; summary: string };
}

/**
 * Call the Firebase function and return a fully-populated
 * {@link FengShuiRanking}, ready to hand to `appendRanking`. Pre-flight
 * mirrors {@link requestFengShuiLayout} exactly: requires Firebase to be
 * configured, the caller to be signed in, and every existing piece to fit
 * its room/wall. Throws the same error classes the layout path uses so
 * callers can share the inline alert UI.
 */
export async function requestFengShuiRanking(
  design: Design
): Promise<FengShuiRanking> {
  if (!isFirebaseConfigured()) throw new FengShuiUnavailableError();
  const app = getFirebaseApp();
  if (!app) throw new FengShuiUnavailableError();

  await requireSessionForAi();

  const offenders = collectInvalidLayoutItems(design);
  if (offenders.length > 0) {
    track("ai_ranking_failed", { reason: "invalid_layout" });
    throw new FengShuiInvalidLayoutError(offenders);
  }

  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const functions = getFunctions(app, getFunctionsRegion());

  // Same denormalization as the layout-generator path so the ranker can
  // reason about which edge of each piece faces the user without a catalog.
  const enrichedFurniture = design.furniture.map((f) => ({
    ...f,
    frontKind: resolveFrontKind(f),
    footprint: wireFootprint(f),
  }));

  const payload: {
    requestId: string;
    room: Design["room"];
    furniture: (Design["furniture"][number] & { frontKind: FrontKind })[];
    mounts: Design["mounts"];
    openings: Design["openings"];
    unit: Design["unit"];
  } = {
    requestId: makeRequestId(),
    room: design.room,
    furniture: enrichedFurniture,
    mounts: design.mounts.filter((m) => !m.interiorWallId),
    openings: design.openings.filter((o) => !o.interiorWallId),
    unit: design.unit,
  };

  // Mirror the layout callable's 180s ceiling; structured-output ranking
  // calls cluster around 8–15s but worst-case can spike on cold start.
  const callable = httpsCallable<
    typeof payload,
    Partial<FengShuiRankingResponse> & { usage?: UsageResponseEnvelope }
  >(functions, "rankFengShuiLayout", { timeout: 180_000 });
  track("ai_ranking_requested");
  const startedAt = Date.now();
  let res;
  try {
    res = await callable(payload);
  } catch (err) {
    const anon = anonymousQuotaReason(err);
    if (anon) {
      track("ai_ranking_failed", { reason: "quota" });
      throw new AiAnonymousQuotaExceededError(anon);
    }
    if (isQuotaExceededError(err)) {
      track("ai_ranking_failed", { reason: "quota" });
      noteQuotaReached("free");
      throw new AiQuotaExceededError();
    }
    if (isFunctionsPermissionDenied(err)) {
      track("ai_ranking_failed", { reason: "premium_required" });
      throw new FengShuiPremiumRequiredError();
    }
    if (isNetworkCallableError(err)) {
      track("ai_ranking_failed", { reason: "network" });
      throw err;
    }
    track("ai_ranking_failed", { reason: "unknown" });
    captureException(err, { context: "requestFengShuiRanking" });
    throw err;
  }
  if (res.data.usage) recordOptimisticUsage(res.data.usage);
  const normalized = normalizeRanking(res.data);
  track("ai_ranking_succeeded", {
    score: Math.round(normalized.overall.score),
    latency_ms: Date.now() - startedAt,
    credits_remaining: creditsRemaining(res.data.usage),
  });
  noteUsageEnvelope(res.data.usage);
  return finalizeRanking(normalized, design);
}

/** Coerce a possibly-partial response into a strict {@link FengShuiRankingResponse}.
 *  Defends against schema drift: missing criteria are filled from
 *  {@link RANKING_CRITERIA_META} with score 0 + a placeholder rationale so the
 *  UI never crashes mid-render. */
function normalizeRanking(
  raw: Partial<FengShuiRankingResponse> | undefined
): FengShuiRankingResponse {
  const r = raw ?? ({} as Partial<FengShuiRankingResponse>);
  const byId = new Map<string, Partial<FengShuiCriterionResult>>();
  for (const c of r.criteria ?? []) {
    if (c && typeof c.id === "string") byId.set(c.id, c);
  }
  const criteria: FengShuiCriterionResult[] = RANKING_CRITERIA_META.map(
    (meta) => {
      const incoming = byId.get(meta.id) ?? {};
      const score = clampScore(incoming.score);
      const observations = Array.isArray(incoming.observations)
        ? incoming.observations.filter(
            (o): o is string => typeof o === "string"
          )
        : [];
      return {
        id: meta.id,
        label:
          typeof incoming.label === "string" && incoming.label.length > 0
            ? incoming.label
            : meta.label,
        weight:
          typeof incoming.weight === "number" &&
          Number.isFinite(incoming.weight)
            ? incoming.weight
            : meta.weight,
        score,
        rationale:
          typeof incoming.rationale === "string" ? incoming.rationale : "",
        observations,
      };
    }
  );

  const overallScore = clampScore(r.overall?.score);
  const summary =
    typeof r.overall?.summary === "string" ? r.overall.summary : "";

  return {
    criteria,
    overall: { score: overallScore, summary },
  };
}

function clampScore(n: unknown): number {
  if (typeof n !== "number" || !Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
}

/** Wrap the server response with the client-generated id/at and a lightweight
 *  snapshot of the layout that was scored. The snapshot keeps each entry
 *  meaningful even after the user rearranges things. */
function finalizeRanking(
  response: FengShuiRankingResponse,
  design: Design
): FengShuiRanking {
  return {
    id: rankingId(),
    at: Date.now(),
    overall: response.overall,
    criteria: response.criteria,
    snapshot: {
      room: design.room,
      openings: design.openings,
      furniture: design.furniture.map((f) => ({
        id: f.id,
        name: f.name,
        category: f.category,
        x: f.x,
        y: f.y,
        rotation: f.rotation,
        width: f.width,
        depth: f.depth,
        height: f.height,
      })),
      mounts: design.mounts.map((m) => ({
        id: m.id,
        name: m.name,
        category: m.category,
        wallIndex: m.wallIndex,
        alongWall: m.alongWall,
        fromFloor: m.fromFloor,
        width: m.width,
        height: m.height,
      })),
    },
  };
}

/** crypto.randomUUID() when available (modern browsers + jsdom 22+),
 *  with a Math.random fallback so the function is safe in older
 *  environments and unit tests that don't expose `crypto`. */
function rankingId(): string {
  if (
    typeof globalThis !== "undefined" &&
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return `rk_${globalThis.crypto.randomUUID()}`;
  }
  return `rk_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(
    36
  )}`;
}

/** Type-checked re-export for places that only need the `Opening` type. */
export type { Opening };
